import { useState, useCallback } from "react";
import useApiCall from "./useApiCall";

/**
 * Custom hook to handle PATCH requests using useApiCall.
 *
 * @param {object} tokens - The authentication tokens for accessing the API.
 * @returns {object} - Object containing response data,
 *  loading state, error state, and request function.
 */
const usePatchRequest = (tokens) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const { makeRequest, isLoading } = useApiCall();

  /**
   * Function to send a PATCH request to the given URL.
   *
   * @param {string} url - The API endpoint URL for the PATCH request.
   * @param {object} body - The request body to be sent.
   * @returns {Promise<any>} - The response data from the API.
   * @throws {Error} - Throws an error if the request fails.
   */
  const sendPatchRequest = useCallback(
    async (url, body) => {
      setError(null);

      try {
        const response = await makeRequest(url, {
          method: "PATCH",
          authTokens: tokens,
          body,
        });

        setData(response || []);
        return response;
      } catch (err) {
        setError(err);
        throw err;
      }
    },
    [tokens, makeRequest]
  );

  return {
    data,
    loading: isLoading,
    error,
    sendPatchRequest,
  };
};

export default usePatchRequest;
